import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, ExternalLink, ImageIcon } from "lucide-react";

const SponsorsManager = () => {
  const [sponsors, setSponsors] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [formData, setFormData] = useState({ name: "", logo_url: "", website_url: "", is_active: true, order: 0 });

  const fetchSponsors = async () => {
    try {
      const res = await axios.get(`${API}/sponsors`, { withCredentials: true });
      setSponsors(res.data);
    } catch (e) {
      // Sponsors fetch failed
    }
  };

  useEffect(() => { fetchSponsors(); }, []);

  const openDialog = (sponsor = null) => {
    if (sponsor) {
      setEditingSponsor(sponsor);
      setFormData({
        name: sponsor.name,
        logo_url: sponsor.logo_url,
        website_url: sponsor.website_url || "",
        is_active: sponsor.is_active,
        order: sponsor.order || 0,
      });
    } else {
      setEditingSponsor(null);
      setFormData({ name: "", logo_url: "", website_url: "", is_active: true, order: sponsors.length });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.logo_url.trim()) {
      toast.error("Name and Logo URL are required");
      return;
    }
    try {
      if (editingSponsor) {
        await axios.put(`${API}/sponsors/${editingSponsor.sponsor_id}`, formData, { withCredentials: true });
        toast.success("Sponsor updated");
      } else {
        await axios.post(`${API}/sponsors`, formData, { withCredentials: true });
        toast.success("Sponsor added");
      }
      setIsDialogOpen(false);
      fetchSponsors();
    } catch (e) {
      toast.error("Failed to save sponsor");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this sponsor?")) return;
    try {
      await axios.delete(`${API}/sponsors/${id}`, { withCredentials: true });
      toast.success("Sponsor removed");
      fetchSponsors();
    } catch (e) {
      toast.error("Failed to remove sponsor");
    }
  };

  return (
    <div data-testid="sponsors-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">Sponsors & Partners</h1>
          <p className="text-cfc-text-muted text-sm mt-1">Manage sponsor logos displayed on the homepage</p>
        </div>
        <Button
          onClick={() => openDialog()}
          className="bg-cfc-accent hover:bg-cfc-accent-hover text-cfc-primary"
          data-testid="add-sponsor-btn"
        >
          <Plus size={18} className="mr-2" /> Add Sponsor
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sponsors.map((sponsor) => (
          <Card key={sponsor.sponsor_id} className="border-cfc-border" data-testid={`sponsor-${sponsor.sponsor_id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className={`font-medium text-sm ${sponsor.is_active ? "text-cfc-primary" : "text-cfc-text-muted"}`}>
                  {sponsor.name}
                </span>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openDialog(sponsor)}>
                    <Edit size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(sponsor.sponsor_id)} className="text-cfc-destructive">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              <div className="bg-white border border-cfc-border rounded p-4 flex items-center justify-center h-20">
                {sponsor.logo_url ? (
                  <img src={sponsor.logo_url} alt={sponsor.name} className="max-h-12 max-w-full object-contain" />
                ) : (
                  <ImageIcon size={24} className="text-cfc-text-muted" />
                )}
              </div>
              {sponsor.website_url && (
                <a href={sponsor.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cfc-text-muted hover:text-cfc-accent flex items-center gap-1 mt-2">
                  <ExternalLink size={10} /> {sponsor.website_url}
                </a>
              )}
            </CardContent>
          </Card>
        ))}
        {sponsors.length === 0 && (
          <div className="col-span-full text-center py-12 text-cfc-text-muted">
            <p>No sponsors yet. Add sponsors to display logos on the homepage.</p>
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent data-testid="sponsor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">
              {editingSponsor ? "Edit Sponsor" : "Add Sponsor"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="sp-name">Sponsor Name</Label>
              <Input id="sp-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Nike" data-testid="sponsor-name-input" />
            </div>
            <div>
              <Label htmlFor="sp-logo">Logo URL</Label>
              <Input id="sp-logo" value={formData.logo_url} onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })} placeholder="https://..." data-testid="sponsor-logo-input" />
              {formData.logo_url && (
                <div className="mt-2 bg-white border rounded p-3 flex items-center justify-center">
                  <img src={formData.logo_url} alt="Preview" className="max-h-12 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="sp-website">Website URL (optional)</Label>
              <Input id="sp-website" value={formData.website_url} onChange={(e) => setFormData({ ...formData, website_url: e.target.value })} placeholder="https://..." data-testid="sponsor-website-input" />
            </div>
            <div>
              <Label htmlFor="sp-order">Display Order</Label>
              <Input id="sp-order" type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch id="sp-active" checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
              <Label htmlFor="sp-active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSubmit} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="save-sponsor-btn">
              {editingSponsor ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SponsorsManager;
